import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Standardized */}
      <section className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Cookie Policy</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              How Enerlligence Analytics uses cookies and tracking technologies across our digital platforms
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <p className="text-slate-600 text-sm">
              Enerlligence Analytics ("Company", "We", "Us", or "Our")
            </p>
            <p className="text-slate-500 text-sm mt-2">
              Effective Date: 1 January 2026 • Last Updated: 1 January 2026
            </p>
          </div>
          <div className="prose prose-lg max-w-none text-slate-700">
            
            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">1. Purpose</h2>
            <p className="mb-4 leading-relaxed">
              This Cookie Policy explains how Enerlligence Analytics uses cookies and similar tracking technologies when users access or interact with our websites, digital platforms, research portals, analytics services, and related online applications (collectively referred to as the "Platform"). As a global information services provider, the Company is committed to ensuring transparency in how data is collected and used while maintaining compliance with applicable international data protection and electronic communications regulations.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">2. What Are Cookies?</h2>
            <p className="mb-4 leading-relaxed">
              Cookies are small text files placed on a user's device (computer, tablet, or mobile device) when visiting a website. Cookies enable websites to recognise devices, enhance functionality, improve user experience, and support analytical insights regarding platform usage. Cookies may be:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Session Cookies</strong> – deleted automatically when the browser is closed; or</li>
              <li><strong>Persistent Cookies</strong> – retained on the device for a defined period or until deleted.</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">3. Types of Cookies We Use</h2>

            <h3 className="text-lg font-semibold text-[#1F3C88] mt-6 mb-3">3.1 Strictly Necessary Cookies</h3>
            <p className="mb-4 leading-relaxed">These cookies are essential for the operation of the Platform and enable core functionalities such as:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Secure login authentication</li>
              <li>Account access and session management</li>
              <li>Network security and fraud prevention</li>
              <li>Navigation across secured areas of the Platform</li>
            </ul>
            <p className="mb-4 leading-relaxed">These cookies cannot be disabled without affecting platform functionality.</p>

            <h3 className="text-lg font-semibold text-[#1F3C88] mt-6 mb-3">3.2 Performance and Analytics Cookies</h3>
            <p className="mb-4 leading-relaxed">These cookies help us understand how users interact with our Platform by collecting aggregated and anonymised information, including:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Website traffic volumes</li>
              <li>Page performance metrics</li>
              <li>User navigation patterns</li>
              <li>System optimisation insights</li>
            </ul>
            <p className="mb-4 leading-relaxed">This information supports continuous improvement of our information services.</p>

            <h3 className="text-lg font-semibold text-[#1F3C88] mt-6 mb-3">3.3 Functionality Cookies</h3>
            <p className="mb-4 leading-relaxed">These cookies enable enhanced user experience by remembering preferences such as:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Language settings</li>
              <li>Region selection</li>
              <li>User interface customisation</li>
              <li>Saved preferences for returning users</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#1F3C88] mt-6 mb-3">3.4 Targeting and Communication Cookies</h3>
            <p className="mb-4 leading-relaxed">Where applicable, these cookies may be used to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Deliver relevant content or industry updates</li>
              <li>Measure effectiveness of communications</li>
              <li>Support professional outreach activities</li>
            </ul>
            <p className="mb-4 leading-relaxed">The Company does not engage in intrusive behavioural advertising practices inconsistent with professional information-service standards.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">4. Third-Party Cookies</h2>
            <p className="mb-4 leading-relaxed">Certain cookies may be placed by trusted third-party service providers supporting our operations, including:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Website analytics providers</li>
              <li>Cloud hosting partners</li>
              <li>Cybersecurity monitoring services</li>
              <li>Content delivery networks</li>
            </ul>
            <p className="mb-4 leading-relaxed">Such providers operate under contractual obligations to maintain appropriate confidentiality and data protection safeguards.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">5. Legal Basis for Cookie Usage</h2>
            <p className="mb-4 leading-relaxed">Where required under applicable law, cookies are deployed based on:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>User consent; or</li>
              <li>Legitimate operational necessity for platform functionality and security.</li>
            </ul>
            <p className="mb-4 leading-relaxed">Users are provided with cookie consent options upon accessing the Platform.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">6. Managing Cookie Preferences</h2>
            <p className="mb-4 leading-relaxed">Users may control or disable cookies through:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Website cookie consent banner settings</li>
              <li>Browser configuration settings</li>
              <li>Device privacy controls</li>
            </ul>
            <p className="mb-4 leading-relaxed">Please note that disabling certain cookies may impact platform performance or access to specific services.</p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">7. Data Protection and Security</h2>
            <p className="mb-4 leading-relaxed">
              Information collected through cookies is processed in accordance with the Company's <Link to="/privacy-policy" className="text-[#007BFF] hover:underline">Privacy Policy</Link> and protected using appropriate technical and organisational safeguards consistent with international information security standards.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">8. International Users</h2>
            <p className="mb-4 leading-relaxed">
              Given the global accessibility of our Platform, cookie-related data may be processed across multiple jurisdictions subject to appropriate cross-border data protection safeguards.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">9. Policy Updates</h2>
            <p className="mb-4 leading-relaxed">
              The Company may update this Cookie Policy periodically to reflect regulatory developments, technological changes, or operational improvements. Updated versions will be published on our website with the revised effective date.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">10. Contact Information</h2>
            <p className="mb-4 leading-relaxed">For enquiries relating to this Cookie Policy or data protection practices, please contact:</p>
            <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 mb-6">
              <p className="font-semibold text-[#1F3C88] mb-2">Data Protection Officer</p>
              <p className="text-slate-600">Enerlligence Analytics</p>
              <p className="text-slate-600">Email: <a href="mailto:privacy@enerlligence.com" className="text-[#007BFF] hover:underline">privacy@enerlligence.com</a></p>
              <p className="text-slate-600">Website: <a href="https://www.enerlligence.com" className="text-[#007BFF] hover:underline">www.enerlligence.com</a></p>
            </div>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">11. Governance Statement</h2>
            <p className="mb-4 leading-relaxed">
              The Company maintains Board and Executive oversight over digital governance, data privacy, and responsible information management practices to ensure continued alignment with international best practices.
            </p>

            {/* Related Links */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-[#1F3C88] mb-4">Related Policies</h3>
              <div className="flex flex-wrap gap-4">
                <Link to="/disclaimer" className="text-[#007BFF] hover:underline">Disclaimer</Link>
                <Link to="/terms-of-service" className="text-[#007BFF] hover:underline">Terms of Service</Link>
                <Link to="/privacy-policy" className="text-[#007BFF] hover:underline">Privacy Policy</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
